import { type ClientVD } from 'vd-server';
import { honoFetcher } from './honoFetcher';

/** Aligned with vd-server `POST /queue/jobs/poll` — only the first N IDs are polled. */
const QUEUE_JOB_POLL_MAX_IDS = 50;

/** Hard cap so the UI never waits unbounded if jobs stay active (e.g. stuck worker). */
export const POLL_FOR_JOB_DEFAULT_MAX_MS = 10 * 60 * 1000;

/**
 * Dataset test runs can take much longer than typical background jobs
 * (long videos + multi-pass agentic analysis). Keep in sync with
 * `DATASET_TEST_EXPIRE_IN_SECONDS` in vd-server `runDatasetTest.ts`.
 */
export const DATASET_TEST_POLL_MAX_MS = 45 * 60 * 1000;

const pollForJobTimeoutMessage =
  'Timed out waiting for background work to finish. Try refreshing in a moment.';

const TERMINAL = new Set(['completed', 'failed', 'cancelled', 'expired']);

export type PollForJobOptions = {
  maxWaitMs?: number;
  /**
   * Return once this fraction of jobs have finished.
   */
  settleRatio?: number;
  /** Called after each poll with how many jobs have reached a terminal state. */
  onTick?: (settled: number, total: number) => void;
};

export const pollForJob = async (
  client: ClientVD,
  queueName: string,
  jobIds: (string | null)[],
  { maxWaitMs = POLL_FOR_JOB_DEFAULT_MAX_MS, settleRatio, onTick }: PollForJobOptions = {},
) => {
  const ids = jobIds.filter((id): id is string => Boolean(id)).slice(0, QUEUE_JOB_POLL_MAX_IDS);
  if (ids.length === 0) return [];

  const deadline = Date.now() + maxWaitMs;
  const need = settleRatio == null ? ids.length : Math.ceil(ids.length * settleRatio);
  let jobs: { state: string }[] = [];

  do {
    const polled = await honoFetcher(
      client.queue.jobs.poll.$post({ json: { queueName, jobIds: ids } }),
    );
    jobs = polled.jobs;
    onTick?.(jobs.filter((job) => TERMINAL.has(job.state)).length, ids.length);
    if (settleRatio == null) {
      const error = polled.jobs.find((job) => job.error)?.error;
      if (error) throw new Error(error);
    }
    const settled = jobs.filter((job) => TERMINAL.has(job.state)).length;
    if (polled.allTerminal || settled >= need) return jobs;
    if (!polled.timedOut) throw new Error('Unexpected job poll response. Please try again.');
  } while (Date.now() < deadline);

  if (settleRatio != null) return jobs;
  throw new Error(pollForJobTimeoutMessage);
};
